/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match */

import {
  Directive,
  Input,
  ViewContainerRef,
  OnInit,
  OnDestroy,
  ComponentFactoryResolver,
  Output,
  EventEmitter,
  ChangeDetectorRef
} from '@angular/core';
import { Document, DocumentSchema, SchemaField } from '../../store/models/document.model';
import { DataTypeMapService } from './field-type-map.service';
import { DynamicField } from './dynamic-field';
import { ViewComponent, ViewControlType } from 'src/app/modules/management/store/workflow';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, tap, filter } from 'rxjs/operators';

@Directive({
  selector: '[topoDynamicForm]',
  standalone: true
})
export class DynamicFieldDirective implements OnInit, OnDestroy {
  @Input() formView;
  @Input() controlCollection: DynamicField[];
  @Input() schema: DocumentSchema;
  @Input() dataset: Dataset;
  @Input() layerUId: string;
  @Input() configureControl: (control: DynamicField) => void;
  @Output() valueChanged = new EventEmitter<DynamicField>();
  @Input() set document(value: Document) {
    const currentDoc = this.document$.getValue();
    if (!!value) {
      this.document$.next(value);
    }
  }

  @Input() set isEdit(value: boolean) {
    this.isEdit$.next(value);
  }

  document$: BehaviorSubject<Document> = new BehaviorSubject(null);
  isEdit$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  destroy$ = new Subject<void>();

  constructor(
    private view: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    private fieldComponentMap: DataTypeMapService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.createViewControlsFromForm();

    this.document$
      .pipe(
        takeUntil(this.destroy$),
        filter(doc => !!doc),
        tap(doc => {
          this.createViewControlsFromForm();
          this.changeDetector.markForCheck();
        })
      )
      .subscribe();

    this.isEdit$
      .pipe(
        takeUntil(this.destroy$),
        tap(isEdit => {
          this.updateControlValues();
          this.changeDetector.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.clearControlCollection();
    this.destroy$.next();
    this.destroy$.complete();
  }

  createViewControlsFromForm(): void {
    // init the control collection. this is used to expose
    // the form controls to the parent component
    this.clearControlCollection();

    if (!!this.formView && !!this.formView.controls) {
      for (const viewComponent of this.formView?.controls) {
        this.createViewControl(viewComponent);
      }

      this.updateControlValues();
    }
  }

  createViewControl(viewComponent: ViewComponent): void {
    let componentType: any;
    let field: SchemaField = null;

    if (viewComponent.controlType === ViewControlType.DatasetField) {
      field = this.schema?.fields?.find(f => f.uId === viewComponent.dataSetFieldUId);

      const dataType = field?.dataType;

      if (!dataType) {
        return;
      }

      componentType = this.fieldComponentMap.getComponentByDataType(dataType);
    } else {
      if (viewComponent.controlType == ViewControlType.Custom) {
        componentType = this.fieldComponentMap.getComponentByCustomViewControlType(viewComponent.configuration?.customControlType);
      } else {
        componentType = this.fieldComponentMap.getComponentByViewControlType(viewComponent.controlType);
      }
    }

    if (!componentType) {
      return;
    }

    const factory = this.resolver.resolveComponentFactory(componentType);

    // create the component
    const component: any = this.view.createComponent(factory);
    const control = component.instance as DynamicField;
    control.viewComponent = viewComponent;
    control.field = field;
    control.displayName = field?.displayName;

    control.valueChanged
      .pipe(
        takeUntil(this.destroy$),
        tap((field: DynamicField) => {
          this.valueChanged.emit(field);
        })
      )
      .subscribe();

    if (this.configureControl) {
      this.configureControl(control);
    }

    this.controlCollection.push(control);
  }

  updateControlValues(): void {
    for (const control of this.controlCollection) {
      control.document = this.document$.getValue();
      control.isEdit = this.isEdit$.getValue();
      control.layerUId = this.layerUId;
      control.schema = this.schema;
      control.dataset = this.dataset;

      if (control.tpOnContentUpdated) {
        control.tpOnContentUpdated();
      }
    }
  }

  clearControlCollection(): void {
    if (!!this.controlCollection && this.controlCollection.length > 0) {
      this.controlCollection.splice(0, this.controlCollection.length);
    }

    if (!!this.view && this.view.length > 0) {
      for (let i = 0; i < this.view.length; i++) {
        const ref = this.view.get(i);
        ref.destroy();
      }
    }

    this.view.clear();
  }

  onFieldValueChanged(): void {}
}
